import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';

// Commission settings interface
export interface CommissionSettings {
  id: string;
  commissionRate: number; // Percentage (e.g., 10 for 10%)
  lastUpdatedBy: string;
  lastUpdatedAt: Date;
  createdAt: Date;
}

/**
 * Get commission settings from Firebase
 */
export const getCommissionSettings = async (): Promise<CommissionSettings> => {
  try {
    console.log('Getting commission settings...');
    
    const settingsRef = doc(db, 'config', 'commissionSettings');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      console.log('Commission settings not found, creating default settings...');
      
      // Create default settings if they don't exist
      const defaultSettings: Omit<CommissionSettings, 'id'> = {
        commissionRate: 10, // Default 10%
        lastUpdatedBy: 'system',
        lastUpdatedAt: new Date(),
        createdAt: new Date()
      };
      
      await setDoc(settingsRef, {
        ...defaultSettings,
        lastUpdatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      
      return {
        id: 'commissionSettings',
        ...defaultSettings
      };
    }
    
    const data = settingsDoc.data();
    const settings: CommissionSettings = {
      id: settingsDoc.id,
      commissionRate: data.commissionRate || 10,
      lastUpdatedBy: data.lastUpdatedBy || 'unknown',
      lastUpdatedAt: data.lastUpdatedAt?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date()
    };
    
    console.log('Commission settings loaded:', settings);
    return settings;
  } catch (error) {
    console.error('Error getting commission settings:', error);
    // Return default settings if there's an error
    return {
      id: 'default',
      commissionRate: 10,
      lastUpdatedBy: 'system',
      lastUpdatedAt: new Date(),
      createdAt: new Date()
    };
  }
};

/**
 * Update commission settings in Firebase
 */
export const updateCommissionSettings = async (
  commissionRate: number,
  updatedBy: string
): Promise<void> => {
  try {
    console.log('Updating commission settings...', { commissionRate, updatedBy });
    
    // Validate commission rate
    if (commissionRate < 0 || commissionRate > 100) {
      throw new Error('Commission rate must be between 0 and 100');
    }
    
    const settingsRef = doc(db, 'config', 'commissionSettings');
    
    // Get existing settings to preserve createdAt
    const existingDoc = await getDoc(settingsRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : null;
    
    await setDoc(settingsRef, {
      commissionRate,
      lastUpdatedBy: updatedBy,
      lastUpdatedAt: serverTimestamp(),
      createdAt: existingData?.createdAt || serverTimestamp()
    });
    
    console.log('Commission settings updated successfully');
  } catch (error) {
    console.error('Error updating commission settings:', error);
    throw new Error('Failed to update commission settings');
  }
};

/**
 * Get current commission rate (simplified function for use in conversion tracking)
 */
export const getCurrentCommissionRate = async (): Promise<number> => {
  try {
    const settings = await getCommissionSettings();
    return settings.commissionRate;
  } catch (error) {
    console.error('Error getting current commission rate:', error);
    return 10; // Default fallback
  }
}; 